import { formatDistanceToNow } from "date-fns";
import { enGB, sv } from "date-fns/locale";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion, AccordionContent, AccordionItem,
  AccordionTrigger
} from "src/components";
import { theme } from "src/config";
import { User } from "src/models";
import { AuthStore, UserStore, SpaceStore } from "src/stores";
import { UserRole, UserStatus, UserType } from "src/types";
import styled from "styled-components";
import { Avatar, Spacer, Text } from ".";


export interface IMeetingRoomUsersProps {
  userStore?: UserStore;
  spaceStore?: SpaceStore;
  authStore?: AuthStore;
  user?: User;
}

export const MeetingRoomUsers = inject(
  "userStore",
  "authStore",
  "spaceStore",
)(
  observer((props: IMeetingRoomUsersProps) => {
    const { authStore, userStore, user } = props;

    const { t, i18n } = useTranslation();
    const [openAccordions, setOpenAccordions] = React.useState<string[]>(['users','accountless','groups','invited']);

    const toggleAccordionItem = (itemId: string) => {
      let open = [...openAccordions];
      if(open.includes(itemId)) {
        open = open.filter(i => i != itemId);
      } else if(!open.includes(itemId)) {
        open = [...open, itemId];
      }
      setOpenAccordions(open)
    }

    const spaceUsers: User[] = userStore.getUsersBySpace(
      authStore.getCurRoomID,
      "all"
    );

    const users = spaceUsers.filter( u => u.type === UserType.Default)
    const accountless = spaceUsers.filter( u => u.type === UserType.Accountless)
    const groups = spaceUsers.filter( u => u.type === UserType.Group)

    const sortedUsers = users.sort(u => u.isOnline(authStore.getCurRoomID) ? -1 : 0);

    const invited = spaceUsers.filter(
      (u) => u.getStatus(authStore.getCurRoomID) === UserStatus.Invited
    );

    const accepted = sortedUsers.filter(
      (u) => [UserStatus.Accepted, UserStatus.Created].includes(u.getStatus(authStore.getCurRoomID))
    );

    const locale = i18n.language == 'sv' ? sv : enGB;

    return (
      <SidebarInnerContainer>
        <SidebarHeader>
          <Text color="grayDarkest" size="large" weight="bold">
            {t("development")}
          </Text>
          <Spacer size="medium" />
        </SidebarHeader>
        <Accordion type="multiple" value={openAccordions}>
          <AccordionItem value="users">
            <AccordionTrigger onClick={() => toggleAccordionItem('users')}>{t("member_plural")}</AccordionTrigger>
            <AccordionContent>
            <UserList>
              {accepted.map( u => (
                  <ListItem key={u.id}>
                    <Avatar size="medium" leaderBadge={u.canEdit} image={u.getAvatar} backgroundColor = {theme.color.grayDark} initials={u.getInitials} online={u.isOnline(authStore.getCurRoomID)}/>
                    <Spacer size="xsmall" />
                    <TextWrapper>
                      <Text size="small" color={theme.font.color.black}>
                        {u.getFullName}
                      </Text>
                      <Text color={"grayDefault"} size="xsmall">
                        {props.spaceStore.getCurSpace ? t(u.getRole(props.spaceStore.getCurSpace.id)) : ''}
                      </Text>
                      {!u && (
                        <Text color={"grayDefault"} size="xsmall">
                          {t("last_active")}:{" "}
                          {u.last_login
                            ? formatDistanceToNow(new Date(u.lastLogin), {
                                addSuffix: true,
                                locale
                              })
                            : t("unknown")}
                        </Text>
                      )}
                    </TextWrapper> 
                  </ListItem>
              ))}
              </UserList>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="groups">
            <AccordionTrigger onClick={() => toggleAccordionItem('groups')}>{t("group_plural")}</AccordionTrigger>
            <AccordionContent>
            <UserList>
              {groups.map( u => (
                  <ListItem key={u.id}>
                    <Avatar size="medium" leaderBadge={u.canEdit} image={u.getAvatar} backgroundColor = {theme.color.grayDark} initials={u.getInitials} online={u.isOnline(authStore.getCurRoomID)}/>
                    <Spacer size="xsmall" />
                    <TextWrapper>
                      <Text size="small" color={theme.font.color.black}>
                        {u.getFullName}
                      </Text>
                      <Text color={"grayDefault"} size="xsmall">
                        {props.spaceStore.getCurSpace ? t(u.getRole(props.spaceStore.getCurSpace.id)) : ''}
                      </Text>
                      {!u && (
                        <Text color={"grayDefault"} size="xsmall">
                          {t("last_active")}:{" "}
                          {u.last_login
                            ? formatDistanceToNow(new Date(u.lastLogin), {
                                addSuffix: true,
                                locale
                              })
                            : t("unknown")}
                        </Text>
                      )}
                    </TextWrapper> 
                  </ListItem>
              ))}
              </UserList>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="accountless">
            <AccordionTrigger onClick={() => toggleAccordionItem('accountless')}>{t("accountless_users")}</AccordionTrigger>
            <AccordionContent>
            <UserList>
              {accountless.map( u => (
                  <ListItem key={u.id}>
                    <Avatar size="medium" leaderBadge={u.canEdit} image={u.getAvatar} backgroundColor = {theme.color.grayDark} initials={u.getInitials} online={u.isOnline(authStore.getCurRoomID)}/>
                    <Spacer size="xsmall" />
                    <TextWrapper>
                      <Text size="small" color={theme.font.color.black}>
                        {u.getFullName}
                      </Text>
                      <Text color={"grayDefault"} size="xsmall">
                        {props.spaceStore.getCurSpace ? t(u.getRole(props.spaceStore.getCurSpace.id)) : ''}
                      </Text>
                      {!u && (
                        <Text color={"grayDefault"} size="xsmall">
                          {t("last_active")}:{" "}
                          {u.last_login
                            ? formatDistanceToNow(new Date(u.lastLogin), {
                                addSuffix: true,
                                locale
                              })
                            : t("unknown")}
                        </Text>
                      )}
                    </TextWrapper> 
                  </ListItem>
              ))}
              </UserList>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem $hasBottomBorder value="invited">
            <AccordionTrigger onClick={() => toggleAccordionItem('invited')}>{t("members_invited")}</AccordionTrigger>
            <AccordionContent style={{ opacity: 0.5 }}>
              <UserList>
                {invited.map((u) => (
                  <ListItem key={u.id}>
                    <Avatar size="medium" image={u.getAvatar} />
                    <Spacer size="xsmall" />
                    <TextWrapper>
                      <Text color={theme.font.color.black} size="small">
                        {u.getFullName}
                      </Text>
                      <Text color={"grayDefault"} size="xsmall">
                        {t("invited_member")}
                      </Text>
                    </TextWrapper>
                  </ListItem>
                ))}
              </UserList>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </SidebarInnerContainer>
    );
  })
);

const SidebarInnerContainer = styled.div`
  margin: 2rem 2rem;
  z-index: 100;
  max-height: 100vh;
`;

const SidebarHeader = styled.div`
  border-bottom: 1px solid ${theme.color.secondaryBorder};
`;

const UserList = styled.ul`
  margin-bottom: 0.75rem;
`;

const ListItem = styled.li`
  display:        flex;
  flex-direction: row;
  align-items:    center;
  margin-bottom: 0.75rem;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
